// TerrainVoxels（SmoothGrid の固体ボクセル）→ 等値面メッシュの構築。
//
// 高さマップ（terrainGeometry）は列ごとの最高面しか表現できず、崖のせり出しや
// 洞窟（オーバーハング。RJF05 では全列の 19%）が欠落する。ここではボクセルの
// 全立体情報から Surface Nets 法（marching cubes と同系の双対法。セルごとに
// 頂点 1 つを置き、符号が変わるボクセル境界ごとに四角形を張る）で表面を作る。
// 描画（terrainView）と物理（physicsController の trimesh コライダー）の両方が
// この出力を使い、見た目と当たり判定を完全に一致させる。
//
// 既知の限界: 基本 Surface Nets（1 セル 1 頂点）は、固体が対角だけで接する
// 曖昧ケースで非多様体辺（3 枚以上の三角形が共有する辺）を生じうる
// （実測: RJF05 で 96 本）。境界辺・穴・向きの不整合は無く、three の描画も
// Rapier の trimesh も多様体性を要求しないため実害はない（穴・破綻ではない）。
//
// 表面の高さ校正:
// 実機の smooth terrain の等値面は占有率から単純補間した面より系統的に高い。
// 上向きの面（固体 → 上が空気）の交点高さには、実測 2 点（低占有セル +0.7 =
// RJF02 SpawnLocation で接地 / 満杯セル境界 +2 = RJF05/RJF09 で接地配置された
// 設置物 440 件の浮き量の集中値）に合わせ済みの列高さ式
//   top = base + 4·min(1, occ + 0.7) + 2·max(0, (occ − 0.3) / 0.7)
// をそのまま使う（rbxl-to-scene.luau の heights と同じ式。開けた地面では
// 高さマップと同一の面になり、接地校正が退行しない）。
// 横・下向きの面は占有率の線形補間で配置する（壁・天井は校正データが無いため
// 標準の等値面位置。満杯ボクセルでは境界の中点 = 実機より控えめな膨らみ）。
import { TerrainDescriptor, TerrainVoxels } from './types'

/** ボクセル一辺（studs）。SmoothGrid 固定値（TerrainDescriptor.cellSize と同じ）。 */
const VOXEL_STUDS = 4

/**
 * 上向き交点の、空気側コーナーへの食い込み防止マージン（studs）。
 * 満杯セルの校正高さ（境界 +2）はちょうど空気ボクセル中心 = 辺の端点なので、
 * 端点に乗ると四角形が退化する。わずかに内側へクランプする。
 */
const EDGE_CLAMP_MARGIN = 0.01

export interface TerrainIsosurfaceData {
  /** 頂点座標 (x, y, z) フラット配列（ワールド studs）。 */
  positions: Float32Array
  /** 三角形インデックス。 */
  indices: Uint32Array
  /** 頂点ごとの素材（palette インデックス）。頂点カラーの塗り分け用。 */
  vertexMaterials: Uint8Array
}

/** RLE を展開したボクセルグリッド。 */
interface VoxelGrid {
  readonly minX: number
  readonly minY: number
  readonly minZ: number
  readonly sizeX: number
  readonly sizeY: number
  readonly sizeZ: number
  /** 占有率 0..255（0 = 空気）。線形 index = (y * sizeZ + z) * sizeX + x。 */
  readonly occupancy: Uint8Array
  /** 素材（palette インデックス + 1。0 = 空気）。 */
  readonly material: Uint8Array
}

/** RLE ラン（[個数, 素材, 占有率] × N）を展開する。 */
const decodeVoxels = (voxels: TerrainVoxels): VoxelGrid => {
  const volume = voxels.sizeX * voxels.sizeY * voxels.sizeZ
  const occupancy = new Uint8Array(volume)
  const material = new Uint8Array(volume)
  let cursor = 0
  for (let i = 0; i < voxels.runs.length; i += 3) {
    const count = voxels.runs[i]
    const mat = voxels.runs[i + 1]
    const occ = voxels.runs[i + 2]
    if (mat !== 0 && occ !== 0) {
      occupancy.fill(occ, cursor, cursor + count)
      material.fill(mat, cursor, cursor + count)
    }
    cursor += count
  }
  return {
    minX: voxels.minX,
    minY: voxels.minY,
    minZ: voxels.minZ,
    sizeX: voxels.sizeX,
    sizeY: voxels.sizeY,
    sizeZ: voxels.sizeZ,
    occupancy,
    material,
  }
}

/**
 * 上向きの面の交点高さ（固体ボクセル base からの相対 studs）。
 * rbxl-to-scene.luau の heights と同じ実測校正式。
 */
const calibratedFillHeight = (occ255: number): number => {
  const fill = occ255 / 255
  return (
    VOXEL_STUDS * Math.min(1, fill + 0.7) + 2 * Math.max(0, (fill - 0.3) / 0.7)
  )
}

/**
 * 横・下向きの面の、固体コーナー中心から空気コーナー方向への交点距離
 * （studs、0..4）。密度 d = min(1, occ + 0.7) と空気 0 の 0.5 等値面の線形補間。
 */
const lateralCrossingDistance = (occ255: number): number => {
  const density = Math.min(1, occ255 / 255 + 0.7)
  return ((density - 0.5) / density) * VOXEL_STUDS
}

/**
 * ボクセルグリッドから等値面メッシュを構築する。固体ボクセルが無ければ null。
 */
export const buildTerrainIsosurface = (
  terrain: TerrainDescriptor,
): TerrainIsosurfaceData | null => {
  if (!terrain.voxels) {
    return null
  }
  const grid = decodeVoxels(terrain.voxels)
  const { sizeX, sizeY, sizeZ } = grid

  // グリッド外 1 ボクセルの空気の縁を含めて走査する（端の固体にも面が張られ、
  // メッシュが閉じる）。セル座標 c は「コーナー = ボクセル (c..c+1)^3 の中心」。
  const cellsX = sizeX + 1
  const cellsY = sizeY + 1
  const cellsZ = sizeZ + 1

  const occAt = (x: number, y: number, z: number): number => {
    if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) {
      return 0
    }
    return grid.occupancy[(y * sizeZ + z) * sizeX + x]
  }
  const matAt = (x: number, y: number, z: number): number => {
    if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) {
      return 0
    }
    return grid.material[(y * sizeZ + z) * sizeX + x]
  }
  /** ボクセル中心のワールド座標。 */
  const centerX = (x: number): number => (grid.minX + x) * VOXEL_STUDS + 2
  const centerY = (y: number): number => (grid.minY + y) * VOXEL_STUDS + 2
  const centerZ = (z: number): number => (grid.minZ + z) * VOXEL_STUDS + 2

  // --- 頂点: 符号が混在するセルごとに 1 つ（辺交点の平均） -----------------
  // セル (cx,cy,cz) のコーナー = ボクセル (cx-1..cx, cy-1..cy, cz-1..cz)。
  // 全コーナーが固体 or 全コーナーが空気のセルは表面を含まない。
  const cellVertexIndex = new Int32Array(cellsX * cellsY * cellsZ).fill(-1)
  const positions: number[] = []
  const vertexMaterials: number[] = []

  const cellId = (cx: number, cy: number, cz: number): number =>
    (cy * cellsZ + cz) * cellsX + cx

  for (let cy = 0; cy < cellsY; cy += 1) {
    for (let cz = 0; cz < cellsZ; cz += 1) {
      for (let cx = 0; cx < cellsX; cx += 1) {
        // 8 コーナーの固体判定。
        let solidMask = 0
        for (let corner = 0; corner < 8; corner += 1) {
          const vx = cx - 1 + (corner & 1)
          const vy = cy - 1 + ((corner >> 1) & 1)
          const vz = cz - 1 + ((corner >> 2) & 1)
          if (occAt(vx, vy, vz) > 0) {
            solidMask |= 1 << corner
          }
        }
        if (solidMask === 0 || solidMask === 0xff) {
          continue
        }
        // 符号が変わる辺の交点を平均して頂点位置にする。
        let sumX = 0
        let sumY = 0
        let sumZ = 0
        let crossings = 0
        // 素材は固体コーナーのうち最高占有のものから採る。
        let bestOcc = -1
        let bestMat = 0
        for (let corner = 0; corner < 8; corner += 1) {
          const vx = cx - 1 + (corner & 1)
          const vy = cy - 1 + ((corner >> 1) & 1)
          const vz = cz - 1 + ((corner >> 2) & 1)
          const occ = occAt(vx, vy, vz)
          if (occ > bestOcc && occ > 0) {
            bestOcc = occ
            bestMat = matAt(vx, vy, vz)
          }
          // 辺は +x / +y / +z 方向の 3 本だけ見る（セル内 12 辺を重複なく走査）。
          for (let axis = 0; axis < 3; axis += 1) {
            const bit = 1 << axis
            if ((corner & bit) !== 0) {
              continue
            }
            const other = corner | bit
            const ox = cx - 1 + (other & 1)
            const oy = cy - 1 + ((other >> 1) & 1)
            const oz = cz - 1 + ((other >> 2) & 1)
            const occA = occ
            const occB = occAt(ox, oy, oz)
            const solidA = occA > 0
            const solidB = occB > 0
            if (solidA === solidB) {
              continue
            }
            // 交点（固体側から空気側への距離 t studs）。
            const solidOcc = solidA ? occA : occB
            let t: number
            if (axis === 1 && solidA && !solidB) {
              // 上向き（固体の上が空気）: 実測校正済みの列高さ式。
              // 固体ボクセル base = 中心 - 2 からの充填高さを、辺上の
              // 位置（固体中心からの距離）へ変換する。
              t = Math.min(
                VOXEL_STUDS - EDGE_CLAMP_MARGIN,
                calibratedFillHeight(solidOcc) - VOXEL_STUDS / 2,
              )
            } else {
              t = lateralCrossingDistance(solidOcc)
            }
            t = Math.max(EDGE_CLAMP_MARGIN, t)
            const fromX = solidA ? vx : ox
            const fromY = solidA ? vy : oy
            const fromZ = solidA ? vz : oz
            const dirX = solidA ? ox - vx : vx - ox
            const dirY = solidA ? oy - vy : vy - oy
            const dirZ = solidA ? oz - vz : vz - oz
            sumX += centerX(fromX) + dirX * t
            sumY += centerY(fromY) + dirY * t
            sumZ += centerZ(fromZ) + dirZ * t
            crossings += 1
          }
        }
        if (crossings === 0) {
          continue
        }
        cellVertexIndex[cellId(cx, cy, cz)] = positions.length / 3
        positions.push(sumX / crossings, sumY / crossings, sumZ / crossings)
        vertexMaterials.push(Math.max(0, bestMat - 1))
      }
    }
  }

  if (positions.length === 0) {
    return null
  }

  // --- 面: 符号が変わるボクセル境界ごとに、その辺を囲む 4 セルで四角形 -----
  // 軸 a 方向に隣接するボクセル (v, v+a) の符号が異なるとき、その共有面を
  // 囲む 4 セル（残り 2 軸へ -1/0 オフセット）の頂点で 2 三角形を張る。
  // 頂点の巻きは「空気側を向く」ように符号で反転する。
  const indices: number[] = []
  const pushQuad = (
    a: number,
    b: number,
    c: number,
    d: number,
    flip: boolean,
  ): void => {
    if (a < 0 || b < 0 || c < 0 || d < 0) {
      return
    }
    if (flip) {
      indices.push(a, c, b, a, d, c)
    } else {
      indices.push(a, b, c, a, c, d)
    }
  }

  // 各軸方向に隣接するボクセル対（グリッド外 = 空気を含むため -1 から走査）の
  // 符号が異なる境界面ごとに四角形を張る。巻きは「空気側を向く」ように、
  // 固体が +側（面の上側）にあるときだけ反転する。
  for (let vy = -1; vy < sizeY; vy += 1) {
    for (let vz = -1; vz < sizeZ; vz += 1) {
      for (let vx = -1; vx < sizeX; vx += 1) {
        const solid = occAt(vx, vy, vz) > 0
        if (occAt(vx + 1, vy, vz) > 0 !== solid) {
          pushQuad(
            cellVertexIndex[cellId(vx + 1, vy, vz)],
            cellVertexIndex[cellId(vx + 1, vy + 1, vz)],
            cellVertexIndex[cellId(vx + 1, vy + 1, vz + 1)],
            cellVertexIndex[cellId(vx + 1, vy, vz + 1)],
            !solid,
          )
        }
        if (occAt(vx, vy + 1, vz) > 0 !== solid) {
          pushQuad(
            cellVertexIndex[cellId(vx, vy + 1, vz)],
            cellVertexIndex[cellId(vx, vy + 1, vz + 1)],
            cellVertexIndex[cellId(vx + 1, vy + 1, vz + 1)],
            cellVertexIndex[cellId(vx + 1, vy + 1, vz)],
            !solid,
          )
        }
        if (occAt(vx, vy, vz + 1) > 0 !== solid) {
          pushQuad(
            cellVertexIndex[cellId(vx, vy, vz + 1)],
            cellVertexIndex[cellId(vx + 1, vy, vz + 1)],
            cellVertexIndex[cellId(vx + 1, vy + 1, vz + 1)],
            cellVertexIndex[cellId(vx, vy + 1, vz + 1)],
            !solid,
          )
        }
      }
    }
  }

  if (indices.length === 0) {
    return null
  }
  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    vertexMaterials: new Uint8Array(vertexMaterials),
  }
}
